/**
 * Shared AEM Core Carousel behaviour for Swiper-driven `man-*` web components.
 *
 * Publish/preview markup must NOT set `data-cmp-is="carousel"` — that would
 * dual-init AEM Core Carousel JS (`scrollIntoView` on the tabpanel) alongside
 * Swiper. Edit mode keeps `data-cmp-is` so the panel rail can scroll the
 * selected item into view. This helper owns publish deep linking, data layer,
 * active-slide classes, and Swiper editor sync.
 *
 * Deep-link hashes target the carousel root (item list parent), never the
 * slide item. Per-item `#…-tabpanel` hashes make the browser / Core JS
 * scroll the slide, hide the controls, and can hashchange-loop with Swiper
 * loop clones (GKKWM-1280).
 *
 * Adopt this helper in every new Swiper carousel.
 */

export interface CmpCarouselSwiperConfig {
  host: HTMLElement;
  getCarouselRoot: () => HTMLElement | null;
  getSlides: () => HTMLElement[];
  slideTo: (index: number, speed?: number) => void;
}

export interface ApplyDeepLinkOptions {
  loopWarmUp?: boolean;
}

const AUTHOR_LAYER_EVENT = 'man.author-layer-changed';
const PANEL_CONTAINER_MESSAGE = 'cmp.panelcontainer';
const PANEL_CONTAINER_TYPE = 'cmp-carousel';

type AdobeDataLayer = Array<Record<string, unknown>>;

export class CmpCarouselSwiper {
  private readonly config: CmpCarouselSwiperConfig;
  private initializing = true;
  private activeIndex = 0;
  private boundOnHashChange: () => void;
  private boundOnAuthorLayerChanged: (event: Event) => void;
  private editorSubscribed = false;

  constructor(config: CmpCarouselSwiperConfig) {
    this.config = config;
    this.boundOnHashChange = () => this.onHashChange();
    this.boundOnAuthorLayerChanged = (event: Event) => this.onAuthorLayerChanged(event as CustomEvent);
  }

  markReady(): void {
    this.initializing = false;
  }

  isInitializing(): boolean {
    return this.initializing;
  }

  getDeepLinkSlideIndex(): number {
    const hashId = this.getHashId();
    if (!hashId) {
      return -1;
    }

    const slides = this.config.getSlides();
    const exactIndex = slides.findIndex((slide) => slide.id === hashId);
    if (exactIndex >= 0) {
      return exactIndex;
    }

    return slides.findIndex((slide) => Boolean(slide.querySelector(`[id='${cssEscape(hashId)}']`)));
  }

  isCarouselRootHash(): boolean {
    const hashId = this.getHashId();
    const root = this.config.getCarouselRoot();
    return Boolean(hashId && root?.id && hashId === root.id);
  }

  updateUrlHash(): void {
    const carouselId = this.config.getCarouselRoot()?.id;
    if (!carouselId) {
      return;
    }

    const nextHash = `#${carouselId}`;
    if (window.location.hash === nextHash) {
      return;
    }

    history.replaceState(null, '', nextHash);
  }

  refreshActiveSlideClasses(index: number): void {
    const slides = this.config.getSlides();
    const root = this.config.getCarouselRoot();
    const indicators = root?.querySelectorAll('.cmp-carousel__indicator') ?? [];

    slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('cmp-carousel__item--active', isActive);
      slide.toggleAttribute('aria-hidden', !isActive);
    });

    indicators.forEach((indicator, i) => {
      const isActive = i === index;
      indicator.classList.toggle('cmp-carousel__indicator--active', isActive);
      indicator.setAttribute('aria-selected', String(isActive));
      indicator.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  updateCarouselDataLayer(index: number, emitShowEvent = true): void {
    const root = this.config.getCarouselRoot();
    const slide = this.config.getSlides()[index];
    if (!root?.id || !slide) {
      return;
    }

    const dataLayer = getAdobeDataLayer();
    const carouselId = root.id;
    const activeItem = getDataLayerId(slide);

    dataLayer.push({
      component: {
        [carouselId]: { shownItems: undefined },
      },
    });
    dataLayer.push({
      component: {
        [carouselId]: { shownItems: [activeItem] },
      },
    });

    if (emitShowEvent && activeItem) {
      dataLayer.push({
        event: 'cmp:show',
        eventInfo: {
          path: `component.${activeItem}`,
        },
      });
    }
  }

  onSlideChange(index: number): void {
    if (this.initializing) {
      return;
    }

    this.activeIndex = index;
    this.refreshActiveSlideClasses(index);
    this.updateUrlHash();
    this.updateCarouselDataLayer(index);
  }

  scrollHostIntoView(): void {
    if (typeof this.config.host.scrollIntoView === 'function') {
      this.config.host.scrollIntoView({ block: 'nearest' });
    }
  }

  applyDeepLinkOnInit(options?: ApplyDeepLinkOptions): void {
    const index = this.getDeepLinkSlideIndex();
    const slides = this.config.getSlides();
    const startIndex = index > 0 ? index : 0;
    const hadHash = Boolean(this.getHashId());

    if (options?.loopWarmUp && startIndex === 0 && slides.length > 1) {
      // HACK (GKKWM-1219): left looped slide is blank until clones are painted.
      this.config.slideTo(slides.length - 1, 0);
      this.config.slideTo(0, 0);
    } else if (startIndex > 0) {
      this.config.slideTo(startIndex, 0);
    }

    this.activeIndex = startIndex;
    this.refreshActiveSlideClasses(startIndex);

    if (hadHash) {
      this.updateUrlHash();
      this.scrollHostIntoView();
    }
  }

  connect(): void {
    this.initEditorListeners();
    window.addEventListener('hashchange', this.boundOnHashChange);
  }

  disconnect(): void {
    window.removeEventListener('hashchange', this.boundOnHashChange);
    window.removeEventListener(AUTHOR_LAYER_EVENT, this.boundOnAuthorLayerChanged);
  }

  private onHashChange(): void {
    if (this.initializing) {
      return;
    }

    const index = this.getDeepLinkSlideIndex();
    if (index < 0) {
      if (this.isCarouselRootHash()) {
        this.scrollHostIntoView();
      }
      return;
    }

    this.activeIndex = index;
    this.config.slideTo(index, 0);
    this.refreshActiveSlideClasses(index);
    this.updateCarouselDataLayer(index, false);
    this.updateUrlHash();
    this.scrollHostIntoView();
  }

  private getHashId(): string {
    const raw = window.location.hash.replace(/^#/, '');
    if (!raw) {
      return '';
    }

    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  private initEditorListeners(): void {
    if (this.editorSubscribed) {
      return;
    }

    if (!window.Granite?.author?.MessageChannel) {
      return;
    }

    this.editorSubscribed = true;

    window.CQ = window.CQ || {};
    window.CQ.CoreComponents = window.CQ.CoreComponents || {};
    window.CQ.CoreComponents.MESSAGE_CHANNEL =
      window.CQ.CoreComponents.MESSAGE_CHANNEL || new window.Granite.author.MessageChannel('cqauthor', window);

    window.CQ.CoreComponents.MESSAGE_CHANNEL.subscribeRequestMessage(
      PANEL_CONTAINER_MESSAGE,
      (message: { data?: { type?: string; id?: string; operation?: string; index?: number } }) => {
        if (
          message.data?.type === PANEL_CONTAINER_TYPE &&
          this.isEditorMessageForThisCarousel(message.data.id) &&
          message.data.operation === 'navigate' &&
          typeof message.data.index === 'number'
        ) {
          this.activeIndex = message.data.index;
          this.config.slideTo(this.activeIndex, 100);
          this.refreshActiveSlideClasses(this.activeIndex);
        }
      }
    );

    window.addEventListener(AUTHOR_LAYER_EVENT, this.boundOnAuthorLayerChanged);
  }

  private onAuthorLayerChanged(event: CustomEvent<{ layer?: string; prevLayer?: string }>): void {
    if (event.detail?.layer === 'Edit' && event.detail?.prevLayer === 'Preview') {
      this.config.slideTo(this.activeIndex, 100);
    }
  }

  private isEditorMessageForThisCarousel(messageId?: string): boolean {
    if (!messageId) {
      return false;
    }

    const root = this.config.getCarouselRoot();
    const panelId = root?.getAttribute('data-cmp-panelcontainer-id');
    const hostPanelId = this.config.host.getAttribute('data-cmp-panelcontainer-id');

    return messageId === panelId || messageId === hostPanelId || messageId === root?.id;
  }
}

export function getDataLayerId(item: HTMLElement): string {
  const raw = item.dataset.cmpDataLayer;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const [firstKey] = Object.keys(parsed);
      if (firstKey) {
        return firstKey;
      }
    } catch {
      // fall through to element id
    }
  }

  return item.id;
}

/**
 * Extra trailing offset so a multi-slide Swiper can `slideTo` the last panels.
 * Without this, 4 cards at 3-up clamps `slideTo(3)` to index 1 (the 2nd card).
 */
export function trailingSlideOffset(slidesPerView: number, slideSize: number, spaceBetween: number): number {
  const extraSlots = Math.max(0, Math.ceil(slidesPerView) - 1);
  if (extraSlots === 0 || slideSize <= 0) {
    return 0;
  }

  return extraSlots * (slideSize + spaceBetween);
}

function getAdobeDataLayer(): AdobeDataLayer {
  window.adobeDataLayer = window.adobeDataLayer || [];
  return window.adobeDataLayer as unknown as AdobeDataLayer;
}

function cssEscape(value: string): string {
  if (globalThis.CSS?.escape) {
    return globalThis.CSS.escape(value);
  }

  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
